import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { currencyLabel } from '@/lib/utils/currency';
import type { ReportTask } from '../types';
import type { Contract } from './useContractData';

function formatTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
}

interface ReportDataTableProps {
  data: ReportTask[] | undefined;
  selectedContract: Contract;
  totalDurationSeconds: number;
  showBilledPrice?: boolean;
}

export function ReportDataTable({
  data,
  selectedContract,
  totalDurationSeconds,
  showBilledPrice = false,
}: ReportDataTableProps) {
  const { t } = useTranslation();

  /**
   * One printed line per performer.
   *
   * The document used to print one line per parent task carrying the *rolled-up*
   * figures — the parent's own work plus every reviewer adjustment beneath it.
   * Where the reviewer was a different grade, that merged a Партнёр's hour and an
   * Адвокат's hour into a single line attributed to one of them, at an implied
   * rate that appears in no contract (0h 30m for 1,161,263.89 UZS ⇒ 2,322,528/h).
   *
   * Each task and each adjustment is now its own line carrying its OWN duration,
   * billable time and price, so every line has exactly one performer and one
   * rate. The column totals are unchanged: a parent's own figure plus its
   * adjustments' figures is precisely the rollup that used to be printed.
   *
   * Client defect report AL-2026/08-03-01, ND-3 (and R-08.2 for the grade column).
   */
  const rows = (data ?? []).flatMap((task) => {
    const parent = {
      key: `task-${task?.id}`,
      isAdjustment: false,
      task,
      createdAt: task?.createdAt,
      endedAt: task?.endedAt,
      createdByName: task?.createdBy?.name,
      staff: task?.assignedStaff,
      description: task?.description,
      result: task?.result,
      // Own figures only — the adjustments are printed on their own lines below.
      durationInSeconds: task?.durationInSeconds ?? 0,
      billableDurationInSeconds: task?.billableDurationInSeconds ?? 0,
      billedPrice: task?.billedPrice ?? 0,
    };

    const adjustments = (task?.subtasks ?? []).map((adjustment) => ({
      key: `adj-${adjustment?.id}`,
      isAdjustment: true,
      task,
      createdAt: adjustment?.createdAt ?? task?.createdAt,
      endedAt: adjustment?.endedAt ?? task?.endedAt,
      createdByName: task?.createdBy?.name,
      staff: adjustment?.assignedStaff,
      description: task?.description,
      result: task?.result,
      durationInSeconds: adjustment?.durationInSeconds ?? 0,
      billableDurationInSeconds: adjustment?.billableDurationInSeconds ?? 0,
      billedPrice: adjustment?.billedPrice ?? 0,
    }));

    return [parent, ...adjustments];
  });

  return (
    <div className="border rounded-md">
      <Table className="[&_th]:!text-center [&_td]:!text-center [&_th]:align-middle [&_td]:align-middle">
        <TableHeader>
          <TableRow>
            <TableHead className="font-bold border-r" rowSpan={2}>
              {t('#')}
            </TableHead>
            <TableHead className="font-bold border-r" rowSpan={2}>
              {t('date')}
            </TableHead>
            <TableHead className="font-bold border-r" rowSpan={2}>
              {t('createdBy')}
            </TableHead>
            <TableHead className="font-bold border-r" rowSpan={2}>
              {t('assignedTo')}
            </TableHead>
            <TableHead className="font-bold border-r" rowSpan={2}>
              {t('role')}
            </TableHead>
            <TableHead className="font-bold border-r" rowSpan={2}>
              {t('description')}
            </TableHead>
            <TableHead className="font-bold border-r" rowSpan={2}>
              {t('matter')}
            </TableHead>
            <TableHead className="font-bold border-r">{t('runTime')}</TableHead>
            <TableHead className="font-bold border-r" rowSpan={2}>
              {t('taskResultColumn')}
            </TableHead>
            <TableHead className="font-bold border-r" colSpan={2}>
              {t('sendToClient')}
            </TableHead>
            <TableHead className="font-bold border-r" rowSpan={2}>
              {t('showTime')}
            </TableHead>
            {showBilledPrice && (
              <TableHead className="font-bold" rowSpan={2}>
                {t('billedPrice')}
              </TableHead>
            )}
          </TableRow>
          <TableRow>
            <TableHead className="border-r font-bold">
              {formatTime(totalDurationSeconds)}
            </TableHead>
            <TableHead className="border-r font-bold">{t('dateLabel')}</TableHead>
            <TableHead className="border-r font-bold">{t('timeLabel')}</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {rows.length ? (
            rows.map((row, index) => (
              <TableRow key={row.key} className="hover:bg-muted/50">
                <TableCell className="align-top whitespace-pre-wrap break-words border-r">
                  {index + 1}
                </TableCell>
                <TableCell className="align-top whitespace-pre-wrap break-words border-r">
                  {row?.createdAt
                    ? new Date(row.createdAt).toLocaleString('en-GB', {
                        timeZone: 'Asia/Tashkent',
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })
                    : '-'}
                </TableCell>
                <TableCell className="align-top whitespace-pre-wrap break-words border-r">
                  {row?.createdByName ?? '-'}
                </TableCell>
                <TableCell className="align-top whitespace-pre-wrap break-words border-r">
                  {row?.staff?.name ?? '-'}
                </TableCell>
                <TableCell className="align-top whitespace-pre-wrap break-words border-r">
                  {row?.staff?.role?.name ?? '-'}
                </TableCell>
                <TableCell className="align-top whitespace-pre-wrap break-words border-r">
                  {row?.description ?? '-'}
                  {row.isAdjustment && (
                    <span className="ml-1 text-muted-foreground">({t('reviewAdjustment')})</span>
                  )}
                </TableCell>
                <TableCell className="align-top whitespace-pre-wrap break-words border-r">
                  <b>{row?.task?.matter?.name}</b>
                </TableCell>
                <TableCell className="align-top whitespace-pre-wrap break-words border-r">
                  {formatTime(row?.durationInSeconds ?? 0)}
                </TableCell>
                <TableCell className="align-top whitespace-pre-wrap break-words border-r">
                  {row?.result ?? '-'}
                </TableCell>
                <TableCell className="align-top whitespace-pre-wrap break-words border-r">
                  {row?.endedAt ? new Date(row.endedAt).toLocaleDateString() : '-'}
                </TableCell>
                <TableCell className="align-top whitespace-pre-wrap break-words border-r">
                  {row?.endedAt
                    ? new Date(row.endedAt).toLocaleString('en-GB', {
                        timeZone: 'Asia/Tashkent',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })
                    : '-'}
                </TableCell>
                <TableCell className="align-top whitespace-pre-wrap break-words border-r">
                  {formatTime(row?.billableDurationInSeconds ?? 0)}
                </TableCell>
                {showBilledPrice && (
                  <TableCell className="align-top whitespace-pre-wrap break-words border-r">
                    {row?.billedPrice ?? 0} {currencyLabel(selectedContract?.currency)}
                  </TableCell>
                )}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={showBilledPrice ? 13 : 12} className="text-center">
                {t('noDataFound')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
